import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, ShieldCheck, ExternalLink, CheckCircle2, XCircle, FileText, Clock } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { adminListKycQueue, adminDecideKyc, getDocSignedUrl, listKycDecisionCodes } from "@/lib/helper-kyc.functions";
import { TrustBadge } from "@/components/brand/TrustBadge";

export const Route = createFileRoute("/_authenticated/admin/kyc")({
  head: () => ({ meta: [{ title: "KYC review queue — Peopels" }, { name: "robots", content: "noindex" }] }),
  component: AdminKycPage,
});

type Helper = {
  id: string;
  verification_status: string;
  kyc_submitted_at: string | null;
  kyc_reviewed_at: string | null;
  kyc_reviewer_notes: string | null;
  profile: { id: string; full_name: string | null } | null;
};
type Doc = {
  id: string;
  helper_id: string;
  doc_type: string;
  status: string;
  original_filename: string | null;
  created_at: string;
  reviewer_notes: string | null;
  expires_at: string | null;
  issued_at: string | null;
  reference_number: string | null;
};

const DOC_LABEL: Record<string, string> = {
  id_front: "Photo ID (front)",
  id_back: "Photo ID (back)",
  selfie: "Selfie + ID",
  address_proof: "Proof of address",
  right_to_work: "Right to work",
  dbs: "Basic DBS certificate",
  insurance: "Insurance",
  trade_certificate: "Trade certificate",
};

function AdminKycPage() {
  const navigate = useNavigate();
  const list = useServerFn(adminListKycQueue);
  const decide = useServerFn(adminDecideKyc);
  const sign = useServerFn(getDocSignedUrl);
  const loadCodes = useServerFn(listKycDecisionCodes);
  const [loading, setLoading] = useState(true);
  const [helpers, setHelpers] = useState<Helper[]>([]);
  const [docs, setDocs] = useState<Doc[]>([]);
  const [notes, setNotes] = useState<Record<string, string>>({});
  const [codeByHelper, setCodeByHelper] = useState<Record<string, string>>({});
  const [codes, setCodes] = useState<{ code: string; decision: string; label: string }[]>([]);
  const [pending, setPending] = useState<Record<string, boolean>>({});

  const load = async () => {
    try {
      const res = (await list()) as { helpers: Helper[]; docs: Doc[] };
      setHelpers(res.helpers ?? []);
      setDocs(res.docs ?? []);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not load queue");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    supabase.auth.getUser().then(async ({ data, error }) => {
      if (error || !data.user) { navigate({ to: "/auth", search: { redirect: "/admin/kyc" } }); return; }
      await Promise.all([load(), loadCodes().then((r) => setCodes(r.codes as never)).catch(() => {})]);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  async function openDoc(docId: string) {
    try {
      const { url } = await sign({ data: { docId } });
      window.open(url, "_blank", "noopener,noreferrer");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not open document");
    }
  }

  async function onDecide(helperId: string, decision: "approved" | "rejected" | "more_info_required") {
    const code = (codeByHelper[helperId] ?? "").trim();
    if (!code) { toast.error("Pick a reason code first"); return; }
    const codeRow = codes.find((c) => c.code === code);
    if (!codeRow || codeRow.decision !== decision) { toast.error("Reason code does not match this decision"); return; }
    setPending((p) => ({ ...p, [helperId]: true }));
    try {
      await decide({ data: { helperId, decision, decisionCode: code, notes: (notes[helperId] ?? "").trim() || undefined } });
      toast.success(
        decision === "approved" ? "Helper approved"
          : decision === "more_info_required" ? "More info requested from Helper"
          : "Helper rejected",
      );
      await load();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not save decision");
    } finally {
      setPending((p) => ({ ...p, [helperId]: false }));
    }
  }

  return (
    <div className="flex min-h-screen flex-col bg-surface">
      <Header />
      <main id="main-content" className="mx-auto w-full max-w-6xl flex-1 px-4 py-10 sm:px-6 lg:px-8">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <p className="text-sm text-muted-foreground">Admin</p>
            <h1 className="font-display text-3xl font-bold tracking-tight">KYC review queue</h1>
            <p className="mt-1 text-sm text-muted-foreground">Verify Helper identity documents. Decisions are audit-logged and notify the Helper.</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" asChild><Link to="/admin/kyc/emails">Email delivery</Link></Button>
            <Button variant="outline" asChild><Link to="/admin/ops">Ops</Link></Button>
            <Button variant="outline" asChild><Link to="/admin/quality">Quality</Link></Button>
          </div>
        </div>

        {loading ? (
          <div className="mt-16 grid place-items-center"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
        ) : helpers.length === 0 ? (
          <div className="mt-10 rounded-2xl border border-dashed border-border bg-card p-12 text-center">
            <ShieldCheck className="mx-auto h-8 w-8 text-emerald-600" />
            <p className="mt-3 font-display text-lg font-semibold">Queue is clear</p>
            <p className="text-sm text-muted-foreground">No Helpers are awaiting verification right now.</p>
          </div>
        ) : (
          <ul className="mt-8 space-y-4">
            {helpers.map((h) => {
              const hDocs = docs.filter((d) => d.helper_id === h.id);
              return (
                <li key={h.id} className="rounded-2xl border border-border bg-card p-5 shadow-card">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <p className="font-display text-lg font-semibold">{h.profile?.full_name ?? "Helper"}</p>
                      <p className="mt-0.5 text-xs text-muted-foreground">
                        <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" /> Submitted {h.kyc_submitted_at ? new Date(h.kyc_submitted_at).toLocaleString("en-GB") : "—"}</span>
                      </p>
                      <Link
                        to="/admin/kyc/$helperId"
                        params={{ helperId: h.id }}
                        className="mt-1 inline-flex items-center gap-1 text-xs font-medium text-sky-700 hover:underline"
                      >
                        View full timeline →
                      </Link>
                      <div className="mt-2 flex flex-wrap gap-1.5">
                        {h.verification_status === "verified" && <TrustBadge variant="id-verified" />}
                        {h.verification_status === "verified" && hDocs.some((d) => d.doc_type === "dbs" && d.status === "approved") && <TrustBadge variant="verified-professional" />}
                      </div>
                    </div>
                    <StatusPill status={h.verification_status} />
                  </div>

                  {hDocs.length === 0 ? (
                    <p className="mt-4 text-sm text-muted-foreground">No documents uploaded.</p>
                  ) : (
                    <ul className="mt-4 grid gap-2 sm:grid-cols-2">
                      {hDocs.map((d) => (
                        <li key={d.id} className="flex items-center justify-between rounded-lg border border-border bg-background p-2.5">
                          <div className="min-w-0">
                            <p className="truncate text-sm font-medium">{DOC_LABEL[d.doc_type] ?? d.doc_type}</p>
                            <p className="truncate text-xs text-muted-foreground">{d.original_filename ?? d.doc_type} · {d.status}</p>
                            {d.expires_at && (
                              <p className={`mt-0.5 truncate text-xs ${new Date(d.expires_at).getTime() < Date.now() ? "text-destructive" : "text-muted-foreground"}`}>
                                Expires {new Date(d.expires_at).toLocaleDateString("en-GB")}
                              </p>
                            )}
                          </div>
                          <button
                            onClick={() => openDoc(d.id)}
                            className="ml-2 inline-flex shrink-0 items-center gap-1 rounded-md border border-border px-2 py-1 text-xs font-medium hover:bg-accent"
                          >
                            <FileText className="h-3 w-3" /> View <ExternalLink className="h-3 w-3" />
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}

                  <div className="mt-4 space-y-2">
                    <div className="flex flex-wrap items-center gap-2">
                      <label className="text-xs font-medium" htmlFor={`code-${h.id}`}>Reason code (immutable, audit-logged)</label>
                      <select
                        id={`code-${h.id}`}
                        value={codeByHelper[h.id] ?? ""}
                        onChange={(e) => setCodeByHelper((m) => ({ ...m, [h.id]: e.target.value }))}
                        className="h-9 min-w-[260px] flex-1 rounded-md border border-input bg-background px-2 text-sm"
                      >
                        <option value="">Select a reason code…</option>
                        <optgroup label="Approve">
                          {codes.filter((c) => c.decision === "approved").map((c) => (
                            <option key={c.code} value={c.code}>{c.code} — {c.label}</option>
                          ))}
                        </optgroup>
                        <optgroup label="Reject">
                          {codes.filter((c) => c.decision === "rejected").map((c) => (
                            <option key={c.code} value={c.code}>{c.code} — {c.label}</option>
                          ))}
                        </optgroup>
                        <optgroup label="Request more info">
                          {codes.filter((c) => c.decision === "more_info_required").map((c) => (
                            <option key={c.code} value={c.code}>{c.code} — {c.label}</option>
                          ))}
                        </optgroup>
                      </select>
                    </div>
                    <Textarea
                      rows={2}
                      placeholder="Reviewer notes (sent to Helper if rejecting / requesting more info; optional on approve)"
                      value={notes[h.id] ?? ""}
                      onChange={(e) => setNotes((n) => ({ ...n, [h.id]: e.target.value }))}
                      aria-label={`Reviewer notes for ${h.profile?.full_name ?? "helper"}`}
                    />
                    <div className="flex flex-wrap justify-end gap-2">
                      <Button variant="outline" onClick={() => onDecide(h.id, "rejected")} disabled={pending[h.id]}>
                        <XCircle className="mr-1.5 h-4 w-4" /> Reject
                      </Button>
                      <Button variant="outline" onClick={() => onDecide(h.id, "more_info_required")} disabled={pending[h.id]}>
                        <Clock className="mr-1.5 h-4 w-4" /> Request more info
                      </Button>
                      <Button onClick={() => onDecide(h.id, "approved")} disabled={pending[h.id]}>
                        {pending[h.id] ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <CheckCircle2 className="mr-1.5 h-4 w-4" />}
                        Approve
                      </Button>
                    </div>
                  </div>

                  {h.kyc_reviewer_notes && (
                    <p className="mt-3 rounded-md bg-accent/40 p-2 text-xs text-muted-foreground">
                      <span className="font-medium text-foreground">Last reviewer note:</span> {h.kyc_reviewer_notes}
                    </p>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </main>
      <Footer />
    </div>
  );
}

function StatusPill({ status }: { status: string }) {
  const map: Record<string, string> = {
    pending: "bg-amber-100 text-amber-900",
    verified: "bg-emerald-100 text-emerald-900",
    rejected: "bg-destructive/10 text-destructive",
    unverified: "bg-muted text-muted-foreground",
  };
  return <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium capitalize ${map[status] ?? "bg-muted text-muted-foreground"}`}>{status}</span>;
}
